// ProductsFilters.tsx
import React, { useEffect, useState } from "react";
import { Progress } from "../../components/ui/progress";
import {
  Search,
  CalendarIcon,
  ArrowUpDown,
  ChevronDown,
  Tag,
  Wand2,
  Store,
  X,
  Loader2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
} from "../../components/ui/select";
import { format } from "date-fns";
import { useSyncShopify } from "../../hooks/useSyncShopify";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { Badge } from "../../components/ui/badge";
import { useSelector, useDispatch } from "react-redux";
import {
  setSearchKeyword,
  setSelectedTags,
  setDateRange,
  setSortBy,
  clearFilters,
  setFilters,
  setQualityGroup,
  FiltersState,
} from "../../store/filtersSlice";
import type { RootState } from "../../store/store";
import type { QualityGroup } from "../../types/api";
import { deslugify } from "../../utils";
import { setEnrichmentDialogOpen } from "../../store/flowSlice";
import { api } from "../../services/api";
import { useTranslation } from "../../i18n/hooks";

// Type definitions
export const SORT_OPTIONS = {
  "title-asc": "sort_title_asc",
  "title-desc": "sort_title_desc",
  "created_at-desc": "sort_created_desc",
  "created_at-asc": "sort_created_asc",
} as const;

export type SortOptionKey = keyof typeof SORT_OPTIONS;

type DateRange = {
  from: Date;
  to?: Date;
} | null;

const qualityGroupOptions = Object.keys({
  all_products: null,
  missing_images: null,
  missing_description: null,
  missing_tags: null,
  poor_description: null,
  low_seo_score: null,
} as Record<QualityGroup, null>).map((key) => ({
  value: key as QualityGroup,
  label: deslugify(key),
}));

// Type tanımlamaları
interface CategoryData {
  category_name: string;
  category_hierarchy: string;
  product_type: string;
}

interface CategoryGroup {
  [key: string]: {
    hierarchy: string;
    productTypes: Set<string>;
  };
}

function ProductsFilters() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {fetchingProducts, lastFilteredProductCount} = useSelector((state: RootState) => state.flow);
  const { currentCollection } = useSelector((state: RootState) => state.user);
  const filters = useSelector((state: RootState) => state.filters);
  const {
    searchKeyword,
    selectedTags,
    date,
    sortBy,
    qualityGroup,
    categoryHierarchy,
    categoryName,
    productType,
  } = filters;
  const { syncShopify, isSyncing, progress, error } = useSyncShopify({
    onCompleted: () => {
      window.location.reload();
    },
  });
  const [localSearch, setLocalSearch] = useState(searchKeyword);
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup>({});
  const [availableProductTypes, setAvailableProductTypes] = useState<string[]>(
    []
  );
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  const getActiveFilters = (): string[] => {
    const activeFilters: string[] = [];

    if (searchKeyword) {
      activeFilters.push(`${t('productsView.filter_search')} ${searchKeyword}`);
    }

    selectedTags.forEach((tag) => {
      activeFilters.push(`${t('productsView.filter_tag')} ${tag}`);
    });

    if (date?.from && date?.to) {
      activeFilters.push(
        `${t('productsView.filter_date')} ${format(date.from, "MMM d")} - ${format(date.to, "MMM d")}`
      );
    }

    if (qualityGroup && qualityGroup !== "all_products") {
      activeFilters.push(`${t('productsView.filter_quality')} ${deslugify(qualityGroup)}`);
    }

    if (categoryName.length > 0) {
      activeFilters.push(`${t('productsView.filter_category')} ${categoryName[0]}`);
    }
    if (productType.length > 0) {
      activeFilters.push(`${t('productsView.filter_product_type')} ${productType[0]}`);
    }

    return activeFilters;
  };

  const activeFilters = getActiveFilters();

  const handleResetFilters = () => {
    setLocalSearch("");
    setAvailableCategories(Object.keys(categoryGroups));
    
    // Tüm ürün tiplerini topla
    const allProductTypes = new Set<string>();
    Object.values(categoryGroups).forEach(group => {
      group.productTypes.forEach(type => allProductTypes.add(type));
    });
    
    setAvailableProductTypes(["All", ...Array.from(allProductTypes)]);
    dispatch(clearFilters());
  };

  const handleSearchSubmit = (searchValue: string) => {
    dispatch(setSearchKeyword(searchValue));
  };

  const handleSyncClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await syncShopify();
  };

  const shouldShowEnhanceButton = () => {
    return (
      qualityGroup !== "all_products" ||
      (date?.from && date?.to) ||
      !!searchKeyword ||
      categoryName.length > 0 || 
      productType.length > 0 
    );
  };

  const processCategoryData = (data: CategoryData[]) => {
    const groups: CategoryGroup = {};
    const allProductTypes = new Set<string>();

    data.forEach((item) => {
      if (!groups[item.category_name]) {
        groups[item.category_name] = {
          hierarchy: item.category_hierarchy,
          productTypes: new Set<string>(),
        };
      }
      if (item.product_type) {
        groups[item.category_name].productTypes.add(item.product_type);
        allProductTypes.add(item.product_type);
      }
    });

    setCategoryGroups(groups);
    setAvailableCategories(Object.keys(groups));
    setAvailableProductTypes(["All", ...Array.from(allProductTypes)]);
  };

  const handleCategoryChange = (category: string) => {
    if (category) {
      const productTypes = Array.from(
        categoryGroups[category]?.productTypes || []
      );
      setAvailableProductTypes(
        productTypes.length ? ["All", ...productTypes] : ["All"]
      );
    } else {
      setAvailableProductTypes([]);
    }
  };

  const handleProductTypeChange = (productType: string) => {
    if (productType === "All") {
      setAvailableCategories(Object.keys(categoryGroups));
    } else {
      const relevantCategories = Object.entries(categoryGroups)
        .filter(([_, data]) => data.productTypes.has(productType))
        .map(([category]) => category);
      setAvailableCategories(relevantCategories);
    }
  };

  const handleRemoveFilter = (filterText: string) => {
    if (filterText.startsWith(t('productsView.filter_search'))) {
      setLocalSearch("");
      dispatch(setSearchKeyword(""));
    } else if (filterText.startsWith(t('productsView.filter_tag'))) {
      const tag = filterText.replace(`${t('productsView.filter_tag')} `, "");
      dispatch(setSelectedTags(selectedTags.filter(t => t !== tag)));
    } else if (filterText.startsWith(t('productsView.filter_date'))) {
      dispatch(setDateRange(undefined));
    } else if (filterText.startsWith(t('productsView.filter_quality'))) {
      dispatch(setQualityGroup("all_products"));
    } else if (filterText.startsWith(t('productsView.filter_category'))) {
      // Kategori filtresini kaldır ve tüm kategorileri göster
      setAvailableCategories(Object.keys(categoryGroups));
      
      // Ürün tipi filtresini koruyarak kategori filtresini kaldır
      if (productType.length > 0) {
        const relevantCategories = Object.entries(categoryGroups)
          .filter(([_, data]) => data.productTypes.has(productType[0]))
          .map(([category]) => category);
        setAvailableCategories(relevantCategories);
      }
      
      dispatch(setFilters({
        ...filters,
        categoryHierarchy: [],
        categoryName: []
      }));
    } else if (filterText.startsWith(t('productsView.filter_product_type'))) {
      // Ürün tipi filtresini kaldır ve tüm ürün tiplerini göster
      const allProductTypes = new Set<string>();
      Object.values(categoryGroups).forEach(group => {
        group.productTypes.forEach(type => allProductTypes.add(type));
      });
      setAvailableProductTypes(["All", ...Array.from(allProductTypes)]);
      
      // Kategori seçimini koru, sadece ürün tipi filtresini kaldır
      dispatch(setFilters({
        ...filters,
        productType: []
      }));
    }
  };

  async function getCategoryFilterOptions(tableId: string) {
    const response = await api.getCategoryFilterOptions({
      tableId: tableId,
    });

    if (response?.data?.category_group_list) {
      processCategoryData(response.data.category_group_list);
      
      // Mevcut filtreleri kontrol et ve gerekirse güncelle
      if (filters.categoryName.length > 0 || filters.productType.length > 0) {
        const categoryName = filters.categoryName[0] || "";
        const productType = filters.productType[0] || "";
        
        // Kategori ve ürün tipi filtrelerini güncelle
        if (categoryName && !Object.keys(categoryGroups).includes(categoryName)) {
          dispatch(setFilters({
            ...filters,
            categoryHierarchy: [],
            categoryName: [],
            productType: []
          }));
        } else if (productType && categoryName) {
          const categoryData = categoryGroups[categoryName];
          if (categoryData && !categoryData.productTypes.has(productType)) {
            dispatch(setFilters({
              ...filters,
              productType: []
            }));
          }
        }
      }
    }
  }

  useEffect(() => {
    if (currentCollection.collection?.tableId) {
      getCategoryFilterOptions(currentCollection.collection?.tableId);
    }
  }, [currentCollection.collection?.tableId]);

  return (
    <div className="space-y-4 bg-white rounded-xl shadow-md p-6">
      {/* Search and Import */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('productsView.search_placeholder')}
            className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearchSubmit(localSearch);
              }
            }}
          />
        </div>
        {/*         <div className="flex flex-col gap-2">
          <Button 
            variant="outline" 
            className="flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border-gray-200"
            onClick={handleSyncClick}
            disabled={isSyncing}
          >
            {isSyncing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('productsView.importing_progress')} {progress}%
              </>
            ) : (
              <>
                <Store className="h-4 w-4" />
                {t('productsView.import_from_shopify')}
              </>
            )}
          </Button>
          {isSyncing && (
            <Progress value={progress} className="h-2 w-full" />
          )}
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div> */}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Select
          value={qualityGroup}
          onValueChange={(value) =>
            dispatch(setQualityGroup(value as QualityGroup))
          }
        >
          <SelectTrigger className="bg-white border-gray-200">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-gray-500" />
              {qualityGroup && qualityGroup !== "all_products" ? (
                <span>{deslugify(qualityGroup)}</span>
              ) : (
                <span className="text-gray-600">{t('productsView.quality_filter')}</span>
              )}
            </div>
          </SelectTrigger>
          <SelectContent>
            {qualityGroupOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-between border-gray-200 bg-white",
                date && "text-gray-900"
              )}
            >
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-gray-500" />
                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "LLL dd")} -{" "}
                      {format(date.to, "LLL dd")}
                    </>
                  ) : (
                    format(date.from, "LLL dd, y")
                  )
                ) : (
                  <span className="text-gray-600">{t('productsView.date_added')}</span>
                )}
              </div>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={date}
              onSelect={(value) => dispatch(setDateRange(value))}
              numberOfMonths={2}
              className="rounded-md border"
            />
          </PopoverContent>
        </Popover>

        <Select
          value={sortBy || "created_at-desc"}
          onValueChange={(value) => dispatch(setSortBy(value as SortOptionKey))}
        >
          <SelectTrigger className="bg-white border-gray-200">
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <span className="text-gray-600">
                {sortBy
                  ? t(`productsView.${SORT_OPTIONS[sortBy]}`)
                  : t('productsView.sort_created_desc')}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {Object.entries(SORT_OPTIONS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {t(`productsView.${label}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {availableCategories.length > 0 && (
          <Select
            value={categoryName[0] || ""}
            onValueChange={(category) => {
              if (category) {
                const productTypes = Array.from(categoryGroups[category]?.productTypes || []);
                setAvailableProductTypes(productTypes.length ? ["All", ...productTypes] : ["All"]);
                
                dispatch(setFilters({
                  ...filters,
                  categoryHierarchy: categoryGroups[category]?.hierarchy ? [categoryGroups[category].hierarchy] : [],
                  categoryName: [category],
                  productType: []
                }));
              } else {
                setAvailableProductTypes(["All", ...Array.from(new Set(Object.values(categoryGroups).flatMap(group => Array.from(group.productTypes))))]);
                
                dispatch(setFilters({
                  ...filters,
                  categoryHierarchy: [],
                  categoryName: [],
                  productType: []
                }));
              }
            }}
          >
            <SelectTrigger className="bg-white border-gray-200">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-gray-500" />
                {categoryName[0] ? (
                  <span>{categoryName[0]}</span>
                ) : (
                  <span className="text-gray-600">{t('productsView.category_name')}</span>
                )}
              </div>
            </SelectTrigger>
            <SelectContent>
              {availableCategories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select
          value={productType[0] || ""}
          onValueChange={(newProductType) => {
            if (newProductType === "All" || newProductType === "") {
              // Tüm kategorileri göster ama mevcut kategori seçimini koru
              setAvailableCategories(Object.keys(categoryGroups));
              
              // Mevcut kategori seçimini koru, sadece ürün tipi filtresini kaldır
              dispatch(setFilters({
                ...filters,
                productType: []
              }));
            } else {
              const relevantCategories = Object.entries(categoryGroups)
                .filter(([_, data]) => data.productTypes.has(newProductType))
                .map(([category]) => category);

              setAvailableCategories(relevantCategories);

              const currentCategory = categoryName[0] || "";
              const shouldUpdateCategory = !currentCategory || !relevantCategories.includes(currentCategory);
              const newCategory = shouldUpdateCategory ? relevantCategories[0] || "" : currentCategory;

              dispatch(setFilters({
                ...filters,
                categoryHierarchy: categoryGroups[newCategory]?.hierarchy ? [categoryGroups[newCategory].hierarchy] : [],
                categoryName: [newCategory],
                productType: [newProductType]
              }));
            }
          }}
        >
          <SelectTrigger className="bg-white border-gray-200">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-gray-500" />
              {productType[0] ? (
                <span>{productType[0]}</span>
              ) : (
                <span className="text-gray-600">{t('productsView.product_type')}</span>
              )}
            </div>
          </SelectTrigger>
          <SelectContent>
            {availableProductTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-100">
          <span className="text-sm text-gray-500">{t('productsView.active_filters')}</span>
          <div className="flex flex-wrap gap-2 flex-1">
            {activeFilters.map((filter, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="flex items-center gap-1 bg-blue-50 text-blue-700 hover:bg-blue-100"
              >
                {filter}
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={() => handleRemoveFilter(filter)}
                />
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              {t('productsView.clear_all')}
            </Button>
            {shouldShowEnhanceButton() && (
              <Button
              disabled={fetchingProducts || lastFilteredProductCount === 0}
                onClick={() => {
                  dispatch(
                    setEnrichmentDialogOpen({
                      isOpen: true,
                      enrichedProducts: "filtersOnly",
                      selectedFeatures: [
                        "title",
                        "description",
                        "seo",
                        "altText",
                        "productTags",
                      ],
                    })
                  );
                }}
                className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
              >
                <Wand2 className="h-4 w-4" />
                {t('productsView.enhance_with_filters')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsFilters;
